import Fuse from "fuse.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { Button } from "../ui/Button";
import { SectionTitle } from "../ui/SectionTitle";
import { SegmentedControl } from "../ui/SegmentedControl";
import AppCheckbox from "../AppCheckbox";
import CustomIcon from "../CustomIcon";
import { Input } from "../ui/Input";
import {
  IconLayersIntersect,
  IconLayersUnion,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {
  FilterPanelActionBox,
  FilterPanelContent,
  FilterPanelFooter,
  FilterPanelHeader,
  LeftDrawer,
  SelectableItem,
} from "./layout";
import { useTranslation } from "react-i18next";

// A selectable filter item, keyed by a stable string (a topic uid or a tag id)
// with translated display names. The panel is presentation-only and agnostic to
// whether it is showing topics or the tag fallback — the caller supplies the
// items, the current selection, and applies the result. Presented as "topics"
// either way (see RoomDiscoveryPage: the tag fallback is shown as topic filtering).
type FilterItem = {
  key: string;
  translations: PrivateRoom["filterTopicModels"][number]["translations"];
};

// How the selected items combine when filtering the feed. Mirrors the API's
// `topicsMatch`/`tagsMatch` params: "any" → union, "all" → intersection.
type MatchMode = "any" | "all";

// The edit buffer, held as a single object so seeding it on open resets one
// state slot (not "all state", which the react-doctor lint rule forbids).
type Draft = { keys: string[]; match: MatchMode };

export default function TopicFilterPanel({
  items,
  selectedKeys,
  match,
  isOpen,
  onClose,
  onApply,
}: {
  items: FilterItem[];
  // The currently-applied selection + match, used to seed the buffer on open
  // and to order the list (selected items first).
  selectedKeys: string[];
  match: MatchMode;
  isOpen: boolean;
  onClose: () => void;
  onApply: (keys: string[], match: MatchMode) => void;
}) {
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  // Local edit buffer — stays here until Apply lifts it to the caller.
  const [draft, setDraft] = useState<Draft>({ keys: selectedKeys, match });
  const isAllSelected = draft.keys.length === items.length;
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // The visible list is derived, not stored: rebuild the Fuse index only when
  // the item set or display language changes, then filter by the current query
  // in render. An empty query shows every item.
  const fuse = useMemo(
    () =>
      new Fuse(items, {
        includeScore: true,
        threshold: 0.25,
        keys: [`translations.${activeLang}`],
        ignoreDiacritics: true,
      }),
    [items, activeLang]
  );
  const visibleItems =
    searchValue === ""
      ? items
      : fuse.search(searchValue).map((res) => res.item);
  const sortedItems = [...visibleItems].sort((a, b) =>
    getItemTranslation(a).localeCompare(getItemTranslation(b))
  );

  // Re-seed the buffer from the applied selection each time the drawer opens
  // (the caller's applied selection is the source of truth; edits are discarded
  // on cancel simply by re-seeding next open).
  useEffect(() => {
    if (isOpen) setDraft({ keys: selectedKeys, match });
  }, [isOpen]);

  function toggleSelect(item: FilterItem): void {
    setDraft((prev) => ({
      ...prev,
      keys: prev.keys.includes(item.key)
        ? prev.keys.filter((key) => key !== item.key)
        : [...prev.keys, item.key],
    }));
  }

  function selectAll() {
    setDraft((prev) => ({ ...prev, keys: items.map((item) => item.key) }));
  }

  function unselectAll() {
    setDraft((prev) => ({ ...prev, keys: [] }));
  }

  function setMatch(nextMatch: MatchMode) {
    setDraft((prev) => ({ ...prev, match: nextMatch }));
  }

  function handleCloseWithoutSaving() {
    setSearchValue("");
    onClose();
  }

  function handleSubmit() {
    onApply(draft.keys, draft.match);
    setSearchValue("");
    onClose();
  }

  return (
    <LeftDrawer isOpen={isOpen}>
      <FilterPanelHeader>
        <CustomIcon
          className="close-button"
          iconName="close_alt"
          color="var(--color-secondary-bluegrey-7)"
          onClick={handleCloseWithoutSaving}
        />
      </FilterPanelHeader>

      <FilterPanelContent ref={scrollerRef}>
        <span className="info">{t("Filter by topics")}</span>

        <Input
          value={searchValue}
          placeholder={t("Search")}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          trailing={
            searchValue.length === 0 ? (
              <IconSearch className="size-5 text-ds-slate-400" />
            ) : (
              <button
                type="button"
                aria-label={t("Clear search")}
                className="flex size-8 items-center justify-center rounded-full text-ds-slate-400 hover:text-ds-black"
                onClick={() => {
                  setSearchValue("");
                }}
              >
                <IconX className="size-4" />
              </button>
            )
          }
        />

        {searchValue.length === 0 && (
          <div>
            <SectionTitle title={t("Default")} className="my-4" />

            <FilterPanelActionBox>
              <AppCheckbox
                checked={isAllSelected}
                onChange={(checked) => {
                  if (checked) selectAll();
                  else unselectAll();
                }}
              >
                <span className="text-sm font-bold text-primary-black">
                  {t("Select all")}
                </span>
              </AppCheckbox>

              <div
                className={`clear-all ${draft.keys.length > 0 ? "active" : ""}`}
                onClick={unselectAll}
              >
                <CustomIcon iconName="times-radio" />
                {t("Clear all")}
              </div>
            </FilterPanelActionBox>
          </div>
        )}

        <hr style={{ margin: "16px 0" }} />

        <SectionTitle title={t("Topics")} className="my-4" />

        {draft.keys.length >= 2 && (
          <div className="mb-4 flex flex-col gap-2">
            <SegmentedControl<MatchMode>
              value={draft.match}
              onChange={setMatch}
              items={[
                {
                  value: "any",
                  label: t("Any"),
                  icon: <IconLayersUnion className="size-3.5" />,
                  disabled: false,
                  loading: false,
                },
                {
                  value: "all",
                  label: t("All"),
                  icon: <IconLayersIntersect className="size-3.5" />,
                  disabled: false,
                  loading: false,
                },
              ]}
            />
            <span className="text-footnote text-ds-slate-500">
              {draft.match === "any"
                ? t("Articles matching any selected topic")
                : t("Articles matching all selected topics")}
            </span>
          </div>
        )}

        {[
          ...sortedItems.filter((item) => selectedKeys.includes(item.key)),
          ...sortedItems.filter((item) => !selectedKeys.includes(item.key)),
        ].map((item) => {
          const isSelected = draft.keys.includes(item.key);
          return (
            <SelectableItem key={item.key}>
              <span>{getItemTranslation(item, activeLang)}</span>
              <Button
                variant={isSelected ? "secondary" : "outline"}
                size="md"
                onClick={() => {
                  toggleSelect(item);
                }}
              >
                {isSelected ? t("Selected") : t("Select")}
              </Button>
            </SelectableItem>
          );
        })}
      </FilterPanelContent>

      <FilterPanelFooter>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={handleSubmit}
        >
          {t("Apply")}
        </Button>
      </FilterPanelFooter>
    </LeftDrawer>
  );
}
